import { useCallback, useMemo, useRef } from "react";
import type { Dispatch } from "react";
import { audioNotification } from "@components/Common/Notifications/audioNotification";
import type {
  AiInsightsComponentSpec,
  BackendDashboardSpec,
  ComponentSpec,
  DashboardDataResponseSpec,
  MessagePart,
  WidgetResponseSpec,
} from "../../Processing/types";
import { processWidget } from "../../Processing/pipeline";
import { resolveSpecBaselines } from "../../Processing/baselineResolution";
import type { GridWidgetType } from "../../types";
import { USE_MOCK_DATA } from "../../constants";
import type { DataPipeline } from "../../DataPipeline";
import { AiStreamRequestError, type AiStreamFrame } from "../../DataPipeline";
import { framesToParts } from "../aiWidgetFrames";
import {
  CONTROLLER_KINDS,
  bindableMiscs,
  controllerBareId,
  effectiveBinding,
  eligibleByKind,
  listControllers,
  resolveControllerDisplay,
  writeDerivedSlicerKeys,
  type ControllerOption,
} from "../../Processing/controllerBindings";
import { resolveSubjectNames } from "../../Utils/subjectNames";
import { stripTypePrefix } from "../serializeSpec";
import type { DashboardState, DashboardAction, WidgetEntry } from "../dashboardState";

/**
 * Data layer of the dashboard provider: baseline resolution, per-widget processing,
 * and spec/data loading + refresh. Owns the widget-response cache and the
 * just-loaded flag (the latter is returned so the auto-save effect can skip the
 * save that a fresh load would otherwise trigger).
 */
export function useDashboardData(
  state: DashboardState,
  dispatch: Dispatch<DashboardAction>,
  pipeline: DataPipeline,
  allPlayers: any[],
) {
  const widgetResponsesRef = useRef<Record<string, WidgetResponseSpec | null>>({});
  const justLoadedRef = useRef(false);
  // Spec + data of the dashboard as last loaded, so a manual regenerate posts
  // what the user is actually looking at.
  const aiArgsRef = useRef<{
    dashboardId: string;
    backendSpec: BackendDashboardSpec;
    dataResponse: DashboardDataResponseSpec;
  } | null>(null);
  // One in-flight stream per widget id. Starting a stream aborts the previous
  // one for that id (load vs autosave-sync race).
  const aiControllersRef = useRef<Record<string, AbortController>>({});
  // Read inside async stream callbacks, which must never see a stale spec.
  const widgetsRef = useRef(state.widgets);
  widgetsRef.current = state.widgets;
  // Held in refs so processAndStore keeps a stable identity as widgets change.
  const controllersRef = useRef<ControllerOption[]>([]);
  controllersRef.current = useMemo(
    () => listControllers(Object.values(state.widgets)),
    [state.widgets],
  );
  // Refetches can overlap when edits land close together. Only the newest one may write,
  // so an older response never lands on top of newer data.
  const refreshSeqRef = useRef(0);

  const resolveBaselines = useCallback(
    (spec: ComponentSpec): ComponentSpec =>
      resolveSpecBaselines(spec, allPlayers, pipeline.getMetrics()),
    [allPlayers, pipeline],
  );

  const resolveNames = useCallback(
    (subjects: string[], drills: string[]) =>
      resolveSubjectNames(subjects, drills, pipeline.getSubjectEntities),
    [pipeline],
  );

  const processAndStore = useCallback(
    (
      widget: WidgetEntry,
      responseOverride?: WidgetResponseSpec | null,
      controllersOverride?: ControllerOption[],
    ) => {
      try {
        const rawSpec = widget.spec ?? pipeline.resolveSpec(widget.type);
        // A governed widget must render its CONTROLLER's dates/subjects in its labels,
        // matching the values the engine substituted into its data. The controller
        // display wraps the baseline-resolved spec, so both substitutions apply.
        const resolvedSpec = resolveControllerDisplay(
          resolveBaselines(rawSpec),
          controllersOverride ?? controllersRef.current,
          resolveNames,
          pipeline.getSubjectEntities,
        );
        const response = responseOverride !== undefined
          ? responseOverride
          : widget.spec
            ? pipeline.resolveResponseForSpec(resolvedSpec, widget.id)
            : pipeline.resolveResponse(widget.type, widget.id);
        widgetResponsesRef.current[widget.id] = response;
        // A per-widget error from the backend (e.g. a gauge with no stored baseline)
        // arrives inside an otherwise-successful dashboard response. Route it to the
        // widget's error state — otherwise it processes as empty data and renders a
        // misleading "no data in range" instead of the actual failure. The exception
        // is a response flagged `no_data`: an empty window is exactly what the
        // widget's own "no data in range" state is for, so it processes normally.
        if (response?.error && !response.no_data) {
          dispatch({
            type: "SET_WIDGET_ERROR",
            widgetId: widget.id,
            error: response.error,
          });
          return;
        }
        const processed = processWidget(resolvedSpec, response);
        dispatch({
          type: "SET_WIDGET_DATA",
          widgetId: widget.id,
          data: processed,
          status: "ready",
        });
      } catch (err) {
        dispatch({
          type: "SET_WIDGET_ERROR",
          widgetId: widget.id,
          error: String(err),
        });
      }
    },
    [pipeline, resolveBaselines, resolveNames],
  );

  const patchWidgetDisplaySpec = useCallback(
    (widgetId: string, spec: ComponentSpec) => {
      dispatch({ type: "UPDATE_WIDGET", widgetId, spec });
      const cachedResponse = widgetResponsesRef.current[widgetId];
      processAndStore(
        { id: widgetId, type: spec.type as GridWidgetType, spec },
        cachedResponse ?? null,
      );
    },
    [processAndStore],
  );

  /**
   * Re-label every widget that follows a controller, straight from its cached response.
   *
   * A controller edit changes how its dependents READ without changing their data, and the
   * dependents are not otherwise reprocessed until the autosave-triggered refetch lands.
   * Reprocessing from cache makes the labels track the controller immediately, and works
   * for every widget type because it goes through the same `processWidget` path.
   * Returns the ids of the widgets that follow the controller.
   */
  const reprocessControllerDependents = useCallback(
    (controllerWidgetId: string, nextSpec: ComponentSpec): string[] => {
      const existing = widgetsRef.current[controllerWidgetId];
      if (!existing) return [];
      // The dispatch that carries `nextSpec` has not been applied yet, so splice it in —
      // otherwise the dependents would re-resolve against the controller's OLD values.
      const widgets = { ...widgetsRef.current, [controllerWidgetId]: { ...existing, spec: nextSpec } };
      const controllers = listControllers(Object.values(widgets));
      const uuid = controllerBareId(controllerWidgetId);
      const eligible = eligibleByKind(controllers);
      const followers: string[] = [];

      for (const widget of Object.values(widgets)) {
        const follows = bindableMiscs(widget.spec).some((misc) =>
          CONTROLLER_KINDS.some((kind) => effectiveBinding(misc, kind, eligible[kind]) === uuid),
        );
        if (!follows) continue;
        followers.push(widget.id);
        processAndStore(widget, widgetResponsesRef.current[widget.id] ?? null, controllers);
      }
      return followers;
    },
    [processAndStore],
  );

  const refreshData = useCallback(async (
    savedSpec?: BackendDashboardSpec,
  ): Promise<DashboardDataResponseSpec | null> => {
    const seq = ++refreshSeqRef.current;
    const superseded = () => seq !== refreshSeqRef.current;
    try {
      if (USE_MOCK_DATA || !state.dashboardId) return null;
      const dataResponse = await pipeline.getDashboardData(state.dashboardId);
      if (superseded()) return null;
      for (const widget of Object.values(state.widgets)) {
        const backendId = stripTypePrefix(widget.id);
        processAndStore(widget, dataResponse.results[backendId] ?? null);
      }
      // Keep the replay args current so a later regenerate sends the spec and
      // data the user is actually looking at. Autosave passes the payload it
      // just persisted, so a widget added after load is in the spec the
      // insights endpoint validates against.
      const backendSpec = savedSpec ?? aiArgsRef.current?.backendSpec;
      if (backendSpec) {
        aiArgsRef.current = { dashboardId: state.dashboardId, backendSpec, dataResponse };
      }
      return dataResponse;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[DashboardProvider] refreshData failed:", err);
      audioNotification.error({
        message: "Dashboard data error",
        description: "Failed to refresh dashboard data. Please try again.",
        placement: "top",
        duration: 5,
      });
      return null;
    } finally {
      // The corner spinners on controller dependents end here, whatever the outcome —
      // unless a newer refetch is still in flight; that one ends them when it lands.
      if (!superseded()) dispatch({ type: "CLEAR_WIDGETS_REFRESHING" });
    }
  }, [state.dashboardId, state.widgets, pipeline, processAndStore]);

  /**
   * Open one AI Insights stream for one tile and dispatch its frames. Never
   * throws: a failure is the tile's error state, not the dashboard's.
   *
   * `force` is sent only by a manual regenerate - editing the query busts the
   * backend cache on save, so load and autosave-sync always send false.
   */
  const requestAiWidget = useCallback(
    async (widgetId: string, force = false) => {
      const args = aiArgsRef.current;
      const spec = widgetsRef.current[widgetId]?.spec as AiInsightsComponentSpec | undefined;
      const query = spec?.query?.trim() ?? "";
      if (!args || !query) return;

      aiControllersRef.current[widgetId]?.abort();
      const controller = new AbortController();
      aiControllersRef.current[widgetId] = controller;

      dispatch({ type: "AI_WIDGET_START", widgetId });

      // Counted here rather than read back off state: the summary.complete
      // fallback needs the count for *this* turn, synchronously.
      let partsAppended = 0;

      const onFrame = (frame: AiStreamFrame) => {
        switch (frame.type) {
          case "step.start": {
            const label = String(frame.label ?? "");
            const detail = frame.detail ? ` \u00b7 ${frame.detail}` : "";
            dispatch({ type: "AI_WIDGET_STEP", widgetId, step: label ? label + detail : null });
            break;
          }
          case "step.done":
            dispatch({ type: "AI_WIDGET_STEP", widgetId, step: null });
            break;
          case "insights_generating":
            dispatch({ type: "AI_WIDGET_STEP", widgetId, step: "Generating insights" });
            break;
          case "text":
            dispatch({ type: "AI_WIDGET_PREVIEW", widgetId, preview: String(frame.text ?? "") || null });
            break;
          case "error":
            dispatch({
              type: "AI_WIDGET_ERROR",
              widgetId,
              error: String(frame.error ?? frame.message ?? "The AI request failed."),
            });
            break;
          case "turn_done":
            dispatch({ type: "AI_WIDGET_DONE", widgetId });
            break;
          default:
            // render / no_data / summary.* / everything ignorable.
            for (const part of framesToParts(frame, partsAppended)) {
              partsAppended += 1;
              dispatch({ type: "AI_WIDGET_PART", widgetId, part });
            }
        }
      };

      try {
        const outcome = await pipeline.streamAiWidget(
          {
            dashboardId: args.dashboardId,
            dashboardSpec: args.backendSpec,
            dashboardData: args.dataResponse,
            widgetId: stripTypePrefix(widgetId),
            force,
          },
          onFrame,
          controller.signal,
        );
        if (outcome === "dropped") {
          dispatch({ type: "AI_WIDGET_ERROR", widgetId, error: "Connection dropped. Please try again." });
        }
      } catch (err) {
        // An abort is the caller superseding or removing this tile - silent.
        if (controller.signal.aborted) return;
        if (err instanceof AiStreamRequestError) {
          // ponytail: a 400 for an empty query can't be told apart from any
          // other 400 without matching prose, and the tile already derives
          // configure-me from an empty local query - so every 400 is an error.
          dispatch({
            type: "AI_WIDGET_ERROR",
            widgetId,
            error: err.status === 403 ? "This team doesn't have DataViz AI enabled." : err.message,
            ...(err.retryAfter != null ? { retryAt: Date.now() + err.retryAfter * 1000 } : {}),
          });
          return;
        }
        // eslint-disable-next-line no-console
        console.error("[DashboardProvider] AI widget stream failed:", err);
        dispatch({ type: "AI_WIDGET_ERROR", widgetId, error: "Failed to reach the AI service." });
      } finally {
        if (aiControllersRef.current[widgetId] === controller) {
          delete aiControllersRef.current[widgetId];
        }
      }
    },
    [dispatch, pipeline],
  );

  /** Every configured AI tile re-queries whenever the dashboard data changes. */
  const requestAllAiWidgets = useCallback(
    (widgets: Record<string, WidgetEntry>) => {
      for (const widget of Object.values(widgets)) {
        if (widget.type === "ai_insights") void requestAiWidget(widget.id);
      }
    },
    [requestAiWidget],
  );

  const abortAiWidget = useCallback((widgetId: string) => {
    aiControllersRef.current[widgetId]?.abort();
    delete aiControllersRef.current[widgetId];
  }, []);

  const abortAllAiWidgets = useCallback(() => {
    for (const controller of Object.values(aiControllersRef.current)) controller.abort();
    aiControllersRef.current = {};
  }, []);

  const loadDashboard = useCallback(
    async (id: string) => {
      abortAllAiWidgets();
      // Never let a previous dashboard's spec leak into this one's AI requests.
      aiArgsRef.current = null;
      dispatch({ type: "LOAD_START" });
      try {
        const specResult = await pipeline.getDashboardSpec(id);
        const widgets: Record<string, WidgetEntry> = {};
        for (const w of specResult.widgets) {
          widgets[w.id] = {
            id: w.id,
            type: w.type as GridWidgetType,
            spec: w.spec,
            meta: w.meta,
          };
        }
        // Every reader derives an unkeyed slicer's key in saved order; the write here
        // makes the first autosave after an edit persist it.
        writeDerivedSlicerKeys(Object.values(widgets));
        justLoadedRef.current = true;
        dispatch({
          type: "LOAD_SUCCESS",
          dashboardId: id,
          name: specResult.name,
          widgets,
          layout: specResult.layout,
        });

        if (USE_MOCK_DATA) {
          for (const widget of Object.values(widgets)) {
            processAndStore(widget);
          }
        } else {
          const dataResponse = await pipeline.getDashboardData(id);
          for (const widget of Object.values(widgets)) {
            const backendId = stripTypePrefix(widget.id);
            const widgetResponse = dataResponse.results[backendId] ?? null;
            processAndStore(widget, widgetResponse);
          }
          if (specResult.backendSpec) {
            aiArgsRef.current = { dashboardId: id, backendSpec: specResult.backendSpec, dataResponse };
            requestAllAiWidgets(widgets);
          }
        }
      } catch (err) {
        dispatch({ type: "LOAD_FAILURE", error: String(err) });
        audioNotification.error({
          message: "Dashboard load error",
          description: "Failed to load dashboard. Please try again.",
          placement: "top",
          duration: 5,
        });
      }
    },
    [pipeline, processAndStore, requestAllAiWidgets, abortAllAiWidgets],
  );

  return {
    processAndStore,
    patchWidgetDisplaySpec,
    reprocessControllerDependents,
    refreshData,
    loadDashboard,
    justLoadedRef,
    requestAiWidget,
    abortAllAiWidgets,
    requestAllAiWidgets,
    abortAiWidget,
  };
}
